import type React from "react";
import type { ComponentPropsWithoutRef, ElementType } from "react";
import type { FieldValues, UseControllerProps } from "react-hook-form";

/** props that almost all of our components should have, with minor exceptions */
export type CommonComponentProps = {
  /** the color scheme to use for the component, defaults to "system" */
  colorScheme?: "system" | "light" | "dark";
  /** is the component preventing user interaction? */
  isDisabled: boolean;
  /** is the component loading for the first time or can't render anything for some period of time? */
  isLoading: boolean;
  /** is the component busy with some action that doesn't affect what's already being displayed, till the action is complete? */
  isBusy: boolean;
};

/** props that are only applicable to certain components */
export type SituationalComponentProps = {
  // IMPROVE: remove this when the new design system exposes setting with to 100% through an atomic CSS class
  isFluid?: boolean;
  // IMPROVE: remove this when the new design system exposes layout configuration through atomic CSS classes
  isInline?: boolean;
  /** is the component in an error state? what is the error? */
  error?: unknown;
  /** when validation fails for a component, this is the message that describes why that failed and what to do to fix it */
  invalidMessage?: string;
  /** merge this component's props with that of the child? */
  asChild?: boolean;
  /** the element to render as */
  as?: ElementType;
};

/** props applicable to a DOM element */
export type ComponentPropsForElement<T extends ElementType = "div"> =
  ComponentPropsWithoutRef<T>;

/** props applicable to a DOM element, but with the specified props removed */
export type ComponentPropsForElementThenOmit<
  T extends ElementType,
  O extends
    | Omit<string, keyof ComponentPropsWithoutRef<T>>
    | keyof ComponentPropsWithoutRef<T>,
> = Omit<ComponentPropsWithoutRef<T>, O & string>;

/** props that are applicable to input components */
export type InputComponentProps<T extends ElementType = "input"> =
  ComponentPropsForElementThenOmit<T, never> &
    Pick<
      ComponentPropsForElement<"input">,
      "name" | "value" | "onChange" | "placeholder"
    > & {
      /** id of the label describing this input */
      labelledById?: string;
    };

/** props that should be removed before application to a DOM element */
export type ComponentPropsNotForElements =
  | keyof CommonComponentProps
  | keyof SituationalComponentProps
  | "color"
  | "intent";

/** props that are applicable to controlled input components (react hook form) */
export type ControlledInputComponentProps<
  T extends FieldValues,
  X extends Partial<{ value: any; onChange: (...args: any[]) => void }>,
> = Omit<X, "value"> &
  Pick<UseControllerProps<T>, "name" | "defaultValue" | "rules"> & {
    /** is the input required? convenience prop to set the appropriate validation rule */
    isRequired?: boolean;
  };

/**
 * Extract the component props out of a component props type that is a union
 * type with various applicable permutations of the `as` prop. This would happen
 * for components that allow customizability of the DOM element rendered. (e.g.
 * as="p" or as="div") This type is useful for components that need to accept
 * the same props of such a component under a specific `as` prop value. (e.g.
 * with as="img" there are attributes only available to such a DOM element
 * versus a `div`)
 * @example
 * ```ts
 *  // a component who's rendered element can be set to "div" or "p"
 *  type SomeComponentProps = SomeComponentBaseProps & (({ as?: "div" } & ComponentPropsWithout<"div", PropsToRemove>) | ({ as?: "p" } & ComponentPropsWithout<"p", PropsToRemove>));
 *  // then we extract the prop type of that component for when the "as" prop is set to "p"
 *  type ExtractedSomeComponentProps = ComponentPropsAs<SomeComponentProps, "p">;
 * ```
 */
export type ComponentPropsAs<
  C extends React.ElementType<any>,
  T extends React.ComponentPropsWithoutRef<C>["as"],
> = Omit<
  Extract<React.ComponentPropsWithoutRef<C>, { as: T }>,
  ComponentPropsNotForElements
>;

/**
 * Get the type of the React ref prop for a given element type.
 * @example
 * ```ts
 *  // returns the ref type for a "div" DOM element
 *  ref: PolymorphicRef<"div" | "p", "div">
 * ```
 */
export type PolymorphicRef<T extends ElementType, U extends T> = {
  // @ts-expect-error ElementType is a string here or a string union, but unsure how to make TS happy
  [K in T]: React.ComponentPropsWithRef<K>["ref"];
}[U];

/** sorting settings */
export type SortingType = {
  /** the key to sort by */
  sortBy: string;
  /** the direction to sort by */
  sortDirection?: "asc" | "desc";
};

/** grouping settings */
export type GroupingType = {
  /** the key(s) to group by. order matters. */
  groupBy: string[];
};

/**
 * Strips unrecognized properties from a props object based on the React element
 * type. Only returns props that can be applied to the specified element type.
 * Does not mutate the props - returns a new props object instead. Due to this,
 * this should only be used on props being passed to a native DOM element.
 *
 * This function leverages React's built-in type system to determine valid props.
 * The type safety is enforced at compile time, while runtime filtering provides
 * additional safety and cleaner prop objects.
 *
 * @param props - props object to filter
 * @param elementType - React element type e.g. "input", "div", "button"
 * @returns new props object containing only valid props for the element type
 *
 * @example
 * ```ts
 * const allProps = {
 *   value: "test",
 *   onChange: () => {},
 *   invalidProp: "should be removed",
 *   "data-testid": "my-input",
 *   "data-custom": "preserved"
 * };
 *
 * const cleanProps = stripInvalidPropsForElement(allProps, "input");
 * // Returns: { value: "test", onChange: () => {}, "data-testid": "my-input", "data-custom": "preserved" }
 * ```
 */
export function stripInvalidPropsForElement<T extends ElementType>(
  props: Record<string, any>,
  elementType: T
): ComponentPropsWithoutRef<T> {
  const validProps = {} as ComponentPropsWithoutRef<T>;

  // Extract valid props from the input object
  for (const [key, value] of Object.entries(props)) {
    // Check if the key is a valid prop for the element type
    if (isValidPropForElement(key, elementType)) {
      (validProps as any)[key] = value;
    }
  }

  return validProps;
}

/**
 * Checks if a prop is valid for a given element type. This function uses
 * React's built-in type system for compile-time safety. At runtime, it filters
 * based on common HTML attribute patterns that are hard-coded in here. They are
 * not exhaustive, so use with caution.
 *
 * @param propName - prop name to check
 * @param elementType - React element type e.g. "input", "div", "button"
 * @returns true if the prop is valid for the element type
 */
function isValidPropForElement<T extends ElementType>(
  propName: string,
  elementType: T
): boolean {
  // Common patterns that are always valid for HTML elements
  if (propName.startsWith("aria-") || propName.startsWith("data-")) {
    return true;
  }

  if (propName.startsWith("on") && propName.length > 2) {
    return true;
  }

  // Common HTML attributes that are valid for most elements
  const commonAttributes = new Set([
    "id",
    "className",
    "style",
    "title",
    "lang",
    "dir",
    "tabIndex",
    "accessKey",
    "contentEditable",
    "contextMenu",
    "draggable",
    "dropzone",
    "hidden",
    "spellCheck",
    "translate",
    "role",
  ]);

  if (commonAttributes.has(propName)) {
    return true;
  }

  // Element-specific attributes - simplified non-exhaustive list
  const elementSpecificAttributes: Record<string, Set<string>> = {
    input: new Set([
      "type",
      "value",
      "placeholder",
      "readOnly",
      "disabled",
      "required",
      "autoComplete",
      "autoFocus",
      "form",
      "list",
      "maxLength",
      "minLength",
      "pattern",
      "size",
      "src",
      "alt",
      "accept",
      "capture",
      "checked",
      "dirName",
      "formAction",
      "formEncType",
      "formMethod",
      "formNoValidate",
      "formTarget",
      "height",
      "max",
      "min",
      "multiple",
      "name",
      "step",
      "width",
    ]),
    button: new Set([
      "type",
      "value",
      "name",
      "form",
      "formAction",
      "formEncType",
      "formMethod",
      "formNoValidate",
      "formTarget",
      "disabled",
      "autoFocus",
    ]),
    div: new Set(["align"]),
    form: new Set([
      "action",
      "method",
      "encType",
      "name",
      "noValidate",
      "target",
      "autoComplete",
      "acceptCharset",
    ]),
    textarea: new Set([
      "name",
      "rows",
      "cols",
      "placeholder",
      "readOnly",
      "disabled",
      "required",
      "maxLength",
      "minLength",
      "wrap",
      "form",
      "autoFocus",
      "dirName",
    ]),
    select: new Set([
      "name",
      "size",
      "multiple",
      "required",
      "disabled",
      "form",
      "autoFocus",
    ]),
    label: new Set(["htmlFor", "form"]),
    img: new Set([
      "src",
      "alt",
      "width",
      "height",
      "loading",
      "decoding",
      "isMap",
      "useMap",
      "crossOrigin",
      "referrerPolicy",
      "sizes",
      "srcSet",
    ]),
    a: new Set([
      "href",
      "target",
      "download",
      "rel",
      "hreflang",
      "type",
      "referrerPolicy",
    ]),
  };

  const elementAttrs = elementSpecificAttributes[elementType as string];
  if (elementAttrs && elementAttrs.has(propName)) {
    return true;
  }

  return false;
}

/**
 * Merges props from two objects, with special handling for event handlers,
 * styles, and class names. Inspired by Radix Primitive Slot component's
 * mergeProps implementation.
 *
 * @param slotProps - slot/parent component props. overridden by child props
 * when matching, except for handlers, which are called in order (child first,
 * then slot)
 * @param childProps - child component props. overrides slot props when
 * matching, except for handlers, which are called in order (child first, then
 * slot)
 * @returns merged props object
 *
 * @example
 * ```ts
 * const slotProps = { onClick: () => console.log('slot'), className: 'slot-class' };
 * const childProps = { onClick: () => console.log('child'), className: 'child-class' };
 * const merged = mergeProps(slotProps, childProps);
 * // merged.onClick will call both handlers (child first, then slot)
 * // merged.className will be 'slot-class child-class'
 * ```
 */
export function mergeProps<T extends Record<string, any>>(
  slotProps: T,
  childProps: Record<string, any>
): T & Record<string, any> {
  const overrideProps = { ...childProps };

  for (const propName in childProps) {
    const slotPropValue = slotProps[propName];
    const childPropValue = childProps[propName];
    const isHandler = /^on[A-Z]/.test(propName);

    if (isHandler) {
      if (slotPropValue && childPropValue) {
        overrideProps[propName] = (...args: any[]) => {
          const result = childPropValue(...args);
          slotPropValue(...args);
          return result;
        };
      } else if (slotPropValue) {
        overrideProps[propName] = slotPropValue;
      }
    } else if (propName === "style") {
      overrideProps[propName] = { ...slotPropValue, ...childPropValue };
    } else if (propName === "className") {
      overrideProps[propName] = [slotPropValue, childPropValue]
        .filter(Boolean)
        .join(" ");
    }
  }

  return { ...slotProps, ...overrideProps };
}
